import React, { useCallback, useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  formatLocalDateString,
  getEarliestBookableDate,
  isDateSelectable,
  parseLocalDateString,
} from '../../utils/tourDateValidation';

const SLIDE_DURATION_MS = 220;

function formatMonthYearLabel(date) {
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

/**
 * Date picker that greys out configured months (e.g. July every year)
 * and enforces a minimum business-day buffer from today.
 */
const BlockedMonthDateInput = ({
  id,
  name,
  value,
  onChange,
  required,
  blockedMonths = [],
  minBufferBusinessDays = 0,
  min,
  className = 'form-input',
}) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let minDate = parseLocalDateString(min);
  if (minBufferBusinessDays > 0) {
    const bufferMin = getEarliestBookableDate(minBufferBusinessDays, today);
    minDate = minDate && minDate > bufferMin ? minDate : bufferMin;
  }
  if (!minDate) minDate = today;

  const selected = parseLocalDateString(value);
  const todayIsSelectable = isDateSelectable(today, { blockedMonths, minDate });
  const earliestButtonLabel = todayIsSelectable ? 'Today' : 'Next available';
  const [pickerView, setPickerView] = useState('days');
  const [slideClass, setSlideClass] = useState('');
  const [calendarMonth, setCalendarMonth] = useState(selected || minDate);
  const visibleMonthRef = useRef(selected || minDate);
  const datePickerRef = useRef(null);

  const popperContainer = useCallback(({ children }) => (
    <div
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  ), []);

  useEffect(() => {
    if (selected) {
      setCalendarMonth(selected);
      visibleMonthRef.current = selected;
    }
  }, [selected]);

  const closeCalendar = useCallback(() => {
    datePickerRef.current?.setOpen(false);
    setPickerView('days');
  }, []);

  const filterDate = (date) =>
    isDateSelectable(date, { blockedMonths, minDate });

  const emitChange = (date) => {
    onChange({
      target: {
        name,
        value: date ? formatLocalDateString(date) : '',
      },
    });
  };

  const triggerSlide = useCallback((direction) => {
    setSlideClass(
      direction === 'next'
        ? 'blocked-date-picker-calendar--slide-next'
        : 'blocked-date-picker-calendar--slide-prev'
    );
    window.setTimeout(() => setSlideClass(''), SLIDE_DURATION_MS);
  }, []);

  const handleMonthChange = useCallback((date) => {
    const prev = visibleMonthRef.current;
    if (prev && date.getTime() !== prev.getTime()) {
      triggerSlide(date > prev ? 'next' : 'prev');
    }
    visibleMonthRef.current = date;
    setCalendarMonth(date);
  }, [triggerSlide]);

  const handleDateChange = (date) => {
    if (pickerView === 'months') {
      if (date) {
        handleMonthChange(date);
      }
      setPickerView('days');
      return;
    }
    emitChange(date);
    closeCalendar();
  };

  const handleClear = () => {
    emitChange(null);
    closeCalendar();
  };

  const handleToday = () => {
    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);
    const target = isDateSelectable(todayDate, { blockedMonths, minDate })
      ? todayDate
      : minDate;
    emitChange(target);
    closeCalendar();
  };

  const renderDayHeader = useCallback(
    ({
      date,
      decreaseMonth,
      increaseMonth,
      prevMonthButtonDisabled,
      nextMonthButtonDisabled,
    }) => (
      <div className="blocked-date-picker-header">
        <button
          type="button"
          className="blocked-date-picker-month-btn"
          onClick={() => setPickerView('months')}
          aria-label={`Choose month and year, currently ${formatMonthYearLabel(date)}`}
        >
          <span>{formatMonthYearLabel(date)}</span>
          <span className="blocked-date-picker-chevron" aria-hidden="true">
            ▾
          </span>
        </button>
        <div className="blocked-date-picker-month-nav">
          <button
            type="button"
            className="blocked-date-picker-nav-btn"
            aria-label="Previous month"
            disabled={prevMonthButtonDisabled}
            onClick={() => {
              triggerSlide('prev');
              decreaseMonth();
            }}
          >
            ▲
          </button>
          <button
            type="button"
            className="blocked-date-picker-nav-btn"
            aria-label="Next month"
            disabled={nextMonthButtonDisabled}
            onClick={() => {
              triggerSlide('next');
              increaseMonth();
            }}
          >
            ▼
          </button>
        </div>
      </div>
    ),
    [triggerSlide]
  );

  const renderYearHeader = useCallback(
    ({
      date,
      decreaseYear,
      increaseYear,
      prevYearButtonDisabled,
      nextYearButtonDisabled,
    }) => (
      <div className="blocked-date-picker-header blocked-date-picker-header--year">
        <button
          type="button"
          className="blocked-date-picker-month-btn"
          onClick={() => setPickerView('days')}
          aria-label={`Back to calendar, year ${date.getFullYear()}`}
        >
          <span>{date.getFullYear()}</span>
          <span className="blocked-date-picker-chevron" aria-hidden="true">
            ▾
          </span>
        </button>
        <div className="blocked-date-picker-month-nav">
          <button
            type="button"
            className="blocked-date-picker-nav-btn"
            aria-label="Previous year"
            disabled={prevYearButtonDisabled}
            onClick={() => {
              triggerSlide('prev');
              decreaseYear();
            }}
          >
            ▲
          </button>
          <button
            type="button"
            className="blocked-date-picker-nav-btn"
            aria-label="Next year"
            disabled={nextYearButtonDisabled}
            onClick={() => {
              triggerSlide('next');
              increaseYear();
            }}
          >
            ▼
          </button>
        </div>
      </div>
    ),
    [triggerSlide]
  );

  return (
    <div className="blocked-month-date-input">
      <DatePicker
        ref={datePickerRef}
        id={id}
        selected={selected}
        openToDate={calendarMonth}
        onChange={handleDateChange}
        onMonthChange={handleMonthChange}
        filterDate={filterDate}
        minDate={minDate}
        dateFormat="MM/dd/yyyy"
        placeholderText="mm/dd/yyyy"
        className={className}
        required={required}
        showPopperArrow={false}
        autoComplete="off"
        popperPlacement="bottom-start"
        popperContainer={popperContainer}
        shouldCloseOnSelect={false}
        showMonthYearPicker={pickerView === 'months'}
        renderCustomHeader={
          pickerView === 'days' ? renderDayHeader : renderYearHeader
        }
        calendarClassName={`blocked-date-picker-calendar ${slideClass}`.trim()}
        onCalendarClose={() => {
          setPickerView('days');
        }}
      >
        <div className="react-datepicker__calendar-footer">
          <button
            type="button"
            className="react-datepicker__footer-button"
            onClick={handleClear}
          >
            Clear
          </button>
          <button
            type="button"
            className="react-datepicker__footer-button"
            onClick={handleToday}
          >
            {earliestButtonLabel}
          </button>
        </div>
      </DatePicker>
    </div>
  );
};

export default BlockedMonthDateInput;
